import {
  MdOutlineDashboardCustomize,
  MdCorporateFare,
  MdOutlineAssignment,
  MdOutlineFactCheck,
} from "react-icons/md";
import { SiMarketo } from "react-icons/si";
import { HiOutlineDocumentReport, HiOutlineClipboardList, HiOutlineDocumentText } from "react-icons/hi";
import { FiPieChart, FiLogIn } from "react-icons/fi";
import { FaLayerGroup } from "react-icons/fa";
import { GrDocumentText } from "react-icons/gr";
import { CgProfile } from "react-icons/cg";

/**
 * Menu configuration with main items (with icons) and sub-items (without icons)
 * Each menu item can have:
 * - id: unique identifier
 * - title: display name
 * - to: route path
 * - icon: React icon component (main items only)
 * - children: array of sub-menu items
 * - roles: array of user types that can see this item
 * - visibilityCheck: function to determine visibility based on user context
 */

/**
 * Check if user can see menu item based on roles and conditions
 * @param {Object} item - Menu item
 * @param {Object} user - User context
 * @returns {boolean}
 */
export const isMenuItemVisible = (item, user) => {
  const { userType } = user;

  // If item has custom visibility check, use it
  if (item.visibilityCheck) {
    return item.visibilityCheck(user);
  }

  // If item has roles defined, check if user's role is included
  if (item.roles && item.roles.length > 0) {
    return item.roles.includes(userType);
  }

  // Default: visible to all
  return true;
};

/**
 * Main menu configuration
 * Organized as: Main items with icons → Sub-items without icons
 */
export const MENU_CONFIG = [
  // ===== DASHBOARD (Main) =====
  {
    id: "dashboard",
    title: "Dashboard",
    icon: MdOutlineDashboardCustomize,
    visibilityCheck: (user) => user.userType !== "MH3",
    children: [
      {
        id: "branch-performance-index",
        title: "Branch Performance Index",
        to: "/branch-performance-index",
        visibilityCheck: (user) =>
          user.userType !== "RBDM Collaborator" && user.userType !== "MH3",
      },
      {
        id: "tsi-market-working",
        title: "TSI Market Working FTD",
        to: "/tsi-market-working",
        visibilityCheck: (user) =>
          user.userType !== "RBDM Collaborator" && user.userType !== "MH3",
      },
      {
        id: "distributor-invoicing",
        title: "Distributor Invoicing FTD",
        to: "/distributor-invoicing",
        visibilityCheck: (user) =>
          user.userType !== "RBDM Collaborator" && user.userType !== "MH3",
      },
      {
        id: "key-dealer-performance",
        title: "Key Dealer Performance",
        to: "/key-dealer-performance",
      },
      {
        id: "pil-similar-survey",
        title: "PIL Similar Survey Report",
        to: "/pil-similar-survey",
        visibilityCheck: (user) =>
          user.userType !== "RBDM Collaborator" && user.userType !== "MH3",
      },
    ],
  },

  // ===== MONTHLY TOUR PLAN (Main) =====
  {
    id: "monthly-tour-plan",
    title: "Monthly Tour Plan",
    to: "/monthly-tour-plan",
    icon: SiMarketo,
  },

  // ===== MARKET VISIT REPORT (Main) =====
  {
    id: "market-visit-report",
    title: "Market Visit Report",
    to: "/market-visit-report",
    icon: HiOutlineDocumentReport,
  },

  // ===== MARKET WORKING (Main — expands to sub-items) =====
  {
    id: "key-information",
    title: "Market Working",
    icon: FiPieChart,
    // visibilityCheck: (user) => user.userType !== "MH3",
    children: [
      {
        id: "my-dealer",
        title: "My Dealer",
        to: "/my-dealer",
      },
      {
        id: "my-wss",
        title: "My WSS",
        to: "/my-wss",
      },
      {
        id: "my-user",
        title: "My User",
        to: "/my-user",
      },
      {
        id: "my-team",
        title: "My Team",
        to: "/my-team",
      },
      {
        id: "my-diary",
        title: "My Diary",
        to: "/my-diary",
      },
      {
        id: "queries",
        title: "My Service Request",
        to: "/queries",
      },
    ],
  },

  // ===== SURVEYS (Main — groups Surveys + LCD Form) =====
  {
    id: "surveys",
    title: "Surveys",
    icon: MdOutlineFactCheck,
    children: [
      {
        id: "pil-surveys",
        title: "PIL Surveys",
        to: "/pil-Surveys",
      },
      {
        id: "lcd-form",
        title: "LCD Form",
        to: "/lcdform",
        visibilityCheck: (user) => user.userType === "SH3",
      },
    ],
  },

  // ===== DRM (Main) =====
  {
    id: "drm",
    title: "DRM",
    icon: MdOutlineAssignment,
    children: [
      {
        id: "drm-summary",
        title: "DRM Summary",
        to: "/DRM-Summary",
        visibilityCheck: (user) => {
          const validDivisions = [10, 20, 30, 40, 70];
          const validSalesGroups = [
            101, 105, 107, 111, 112, 201, 202, 210, 301, 302, 303, 401, 406,
            901, 902,
          ];
          return (
            user.userType === "TMM" ||
            ((user.userType === "SH3" || user.userType === "SH4") &&
              (validDivisions.includes(user.userDivision) ||
                validSalesGroups.includes(user.salesGroupCode)))
          );
        },
      },
      {
        id: "drm-planning",
        title: "DRM Planning",
        to: "/drm-planning",
        visibilityCheck: (user) => {
          return (
            user.userType === "SH5" ||
            user.userType === "SH6" ||
            (user.userType === "SH3" &&
              user.salesGroupCode === 105 &&
              user.userDivision === 10)
          );
        },
      },
      {
        id: "drm-meeting-approval",
        title: "DRM Meeting Approval",
        to: "/drm-meeting-approval",
        visibilityCheck: (user) =>
          ["SH7", "SH5", "SH6", "TMM"].includes(user.userType),
      },
    ],
  },

  // ===== DIVISION/SALES GROUP (Main - HO users) =====
  {
    id: "division-sales-group",
    title: "Division/Sales Group",
    icon: FaLayerGroup,
    action: "divisionGroup",
    visibilityCheck: (user) => {
      return (
        (user.userType === "HO Collaborator" ||
          user.userType === "HO Auditor" ||
          user.userType === "TMM" ||
          user.userType === "SH7") &&
        (user.userType === "TMM"
          ? true
          : !user.salesGroupCode || user.userDivision === 70) &&
        user.HO_Role__c !== "RBDM Collaborator" &&
        user.HO_Role__c !== "MH3"
      );
    },
  },

  // ===== PCMS (Main) =====
  {
    id: "pcms",
    title: "PCMS",
    to: "/PCMS",
    icon: MdCorporateFare,
    visibilityCheck: (user) =>
      ["SH3", "SH4", "RBDM Collaborator", "MH3"].includes(user.userType),
  },

  // ===== OAAP (Main — BM/ZM/NSM only) =====
  {
    id: "oaap",
    title: "OAAP",
    to: "/oaap",
    icon: HiOutlineClipboardList,
    visibilityCheck: (user) =>
      ["SH3", "SH4", "SH5", "SH6", "SH7"].includes(user.userType),
  },

  // ===== BM-TSI JC REVIEW (Main — BM/ZM/NSM only) =====
  {
    id: "jc-review",
    title: "BM-TSI JC Review",
    to: "/jc-review",
    icon: HiOutlineDocumentText,
    visibilityCheck: (user) =>
      ["SH3", "SH4", "SH5", "SH6", "SH7"].includes(user.userType),
  },

  // ===== CURRENT DAY TOUR (Main - SH3 only, just above Profile) =====
  {
    id: "current-day-tour",
    title: "Current day tour",
    to: "/current-day-tour",
    icon: GrDocumentText,
    visibilityCheck: (user) => user.userType === "SH3",
  },

  // ===== MY PROFILE (Main) =====
  {
    id: "my-profile",
    title: "My Profile",
    to: "/my-profile",
    icon: CgProfile,
  },

  // ===== LOGOUT (Main) =====
  {
    id: "logout",
    title: "Logout",
    icon: FiLogIn,
    action: "logout",
  },
];

/**
 * Filter menu items based on user context
  },

  // ===== MY PROFILE (Main) =====
  {
    id: "my-profile",
    title: "My Profile",
    to: "/my-profile",
    icon: CgProfile,
  },

  // ===== LOGOUT (Main) =====
  {
    id: "logout",
    title: "Logout",
    icon: FiLogIn,
    action: "logout",
  },
];

/**
 * Filter menu items based on user context
 * @param {Array} menuItems - Menu configuration
 * @param {Object} userContext - User context with role, permissions, etc.
 * @returns {Array} Filtered menu items
 */
export const filterMenuByRole = (menuItems, userContext) => {
  return menuItems
    .map((item) => {
      // Check if main item is visible
      if (!isMenuItemVisible(item, userContext)) {
        return null;
      }

      // If item has children, filter them too
      if (item.children) {
        const visibleChildren = item.children.filter((child) =>
          isMenuItemVisible(child, userContext)
        );

        // Only return item if it has visible children or if it's clickable itself
        if (visibleChildren.length > 0 || item.to || item.action) {
          return {
            ...item,
            children: visibleChildren,
          };
        }
        return null;
      }

      return item;
    })
    .filter(Boolean);
};
